import React, { useEffect, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import { toast } from "../../components/ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card  = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const input = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50";
const label = "text-xs text-[#00ff90]/60 uppercase tracking-wider block mb-1.5";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" });

const TYPE_STYLE = {
  rename: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  split:  "text-purple-400 bg-purple-500/10 border-purple-500/20",
  merge:  "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  move:   "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
};
const STATUS_STYLE = {
  draft:           "text-white/40 bg-white/5 border-white/10",
  impact_assessed: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  approved:        "text-blue-400 bg-blue-500/10 border-blue-500/20",
  applied:         "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  rolled_back:     "text-red-400 bg-red-500/10 border-red-500/20",
};

const _EMPTY = {
  restructure_type: "rename",
  old_department: "",
  new_department: "",
  new_department_2: "",
  merge_from_dept: "",
  description: "",
  effective_date: new Date(Date.now() + 60*86400000).toISOString().slice(0, 10),
};

export default function RestructurePage() {
  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } });
  const isAdmin = !!(user && (user.role === "admin" || user.role === "superadmin" || user.roles?.includes?.("admin")));

  const [items,  setItems]  = useState([]);
  const [busy,   setBusy]   = useState(null);
  const [msg,    setMsg]    = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [draft,  setDraft]  = useState(_EMPTY);
  const [preview, setPreview] = useState(null);   // assessment modal

  const load = async () => {
    const r = await fetch(`${API}/api/workforce/restructures`, { headers: authH() });
    setItems(await r.json());
  };
  useEffect(() => { if (isAdmin) load(); }, []);  // eslint-disable-line

  const create = async () => {
    setBusy("create"); setMsg(null);
    try {
      const body = { ...draft };
      if (body.restructure_type !== "split")  delete body.new_department_2;
      if (body.restructure_type !== "merge")  delete body.merge_from_dept;
      const r = await fetch(`${API}/api/workforce/restructures`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Create failed");
      setMsg({ ok: true, text: "Restructure plan created" });
      setShowForm(false); setDraft(_EMPTY);
      load();
    } catch (e) { setMsg({ ok: false, text: String(e.message || e) }); }
    finally { setBusy(null); }
  };

  const assess = async (id) => {
    setBusy(id);
    try {
      const r = await fetch(`${API}/api/workforce/restructures/${id}/assess`, {
        method: "POST", headers: authH(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setPreview({ id, data });
      const n =
        data?.impact?.affected_employees ??
        data?.affected_employees ?? 0;
      toast.success(`Impact assessed — ${n} employees affected`);
      load();
    } catch (e) {
      toast.error("Failed to assess impact");
    } finally { setBusy(null); }
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      const r = await fetch(`${API}/api/workforce/restructures/${id}/approve`, {
        method: "POST", headers: authH(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Restructure approved");
      setPreview(null);
      load();
    } catch (e) {
      toast.error("Failed to approve");
    } finally {
      setBusy(null);
    }
  };

  const apply = async (id) => {
    if (busy === id) return;
    const item = items.find(x => x.id === id);
    const n = item?.impact_summary?.affected_employees;
    const who = (n != null && n > 0) ? `${n} employee${n === 1 ? "" : "s"}` : "affected employees";
    if (!window.confirm(
      `Apply restructure? This will reassign ${who} to new departments and roles across the organization. It cannot be undone with a single click — rollback requires a separate confirmation. Continue?`
    )) return;
    setBusy(id);
    const r = await fetch(`${API}/api/workforce/restructures/${id}/apply`, {
      method: "POST", headers: authH(), body: JSON.stringify({}),
    });
    const data = await r.json();
    if (r.ok) setMsg({ ok: true, text: `Applied: ${data.employees_updated} employees, ${data.snapshots_taken} snapshots` });
    else      setMsg({ ok: false, text: data.detail || "Apply failed" });
    load(); setBusy(null);
  };

  const rollback = async (id) => {
    if (!window.confirm("Roll back this restructure?")) return;
    setBusy(id);
    const r = await fetch(`${API}/api/workforce/restructures/${id}/rollback`, {
      method: "POST", headers: authH(),
    });
    const data = await r.json();
    if (r.ok) setMsg({ ok: true, text: `Restored ${data.profiles_restored} profiles` });
    else      setMsg({ ok: false, text: data.detail || "Rollback failed" });
    load(); setBusy(null);
  };

  if (!isAdmin) {
    return <MessengerLayout>
      <div className="max-w-3xl mx-auto p-6">
        <div className={`${card} p-6 text-white/60 text-sm`}>Admin access required.</div>
      </div>
    </MessengerLayout>;
  }

  return (
    <MessengerLayout>
      <div className="max-w-5xl mx-auto p-6 space-y-5">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-white">Org Restructure</h1>
            <p className="text-xs text-white/40 mt-1">Plan, assess impact and apply department changes safely (with snapshot rollback).</p>
          </div>
          <button onClick={() => setShowForm(s => !s)}
            className="text-xs px-3 py-2 rounded border border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20">
            {showForm ? "Cancel" : "+ New Restructure"}
          </button>
        </header>

        {msg && <p className={`text-xs ${msg.ok ? "text-[#00ff90]" : "text-red-400"}`}>{msg.text}</p>}

        {showForm && (
          <div className={`${card} p-5 space-y-3`}>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>Type</label>
                <select className={input} value={draft.restructure_type}
                  onChange={e => setDraft(d => ({ ...d, restructure_type: e.target.value }))}>
                  <option value="rename">Rename</option>
                  <option value="split">Split</option>
                  <option value="merge">Merge</option>
                  <option value="move">Move</option>
                </select></div>
              <div><label className={label}>Effective date</label>
                <input type="date" className={input} value={draft.effective_date}
                  onChange={e => setDraft(d => ({ ...d, effective_date: e.target.value }))} /></div>

              <div><label className={label}>Old department</label>
                <input className={input} value={draft.old_department}
                  onChange={e => setDraft(d => ({ ...d, old_department: e.target.value }))} /></div>
              <div><label className={label}>New department</label>
                <input className={input} value={draft.new_department}
                  onChange={e => setDraft(d => ({ ...d, new_department: e.target.value }))} /></div>

              {draft.restructure_type === "split" && (
                <div><label className={label}>Second new department</label>
                  <input className={input} value={draft.new_department_2}
                    onChange={e => setDraft(d => ({ ...d, new_department_2: e.target.value }))} /></div>
              )}
              {draft.restructure_type === "merge" && (
                <div><label className={label}>Merge from department</label>
                  <input className={input} value={draft.merge_from_dept}
                    onChange={e => setDraft(d => ({ ...d, merge_from_dept: e.target.value }))} /></div>
              )}

              <div className="col-span-2"><label className={label}>Description</label>
                <textarea rows={2} className={input} value={draft.description}
                  onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} /></div>
            </div>
            <button onClick={create} disabled={busy === "create" || !draft.old_department || !draft.new_department}
              className="text-xs px-4 py-2 rounded-lg border border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20">
              {busy === "create" ? "Creating…" : "Create plan"}
            </button>
          </div>
        )}

        <section className="space-y-2">
          {items.length === 0 ? <p className="text-white/40 text-sm italic p-3">No restructure plans yet.</p>
            : items.map(r => (
              <div key={r.id} className={`${card} p-4`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${TYPE_STYLE[r.restructure_type] || "border-white/10"}`}>{r.restructure_type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[r.status] || "border-white/10"}`}>{r.status.replace(/_/g, " ")}</span>
                      <span className="text-xs text-white/40">· effective {r.effective_date || "TBD"}</span>
                    </div>
                    <p className="text-sm text-white/85 mt-1">
                      {r.old_department} {r.restructure_type === "merge" && r.merge_from_dept ? `+ ${r.merge_from_dept}` : ""} → <strong>{r.new_department}</strong>
                      {r.restructure_type === "split" && r.new_department_2 ? <> + <strong>{r.new_department_2}</strong></> : null}
                    </p>
                    {r.description && <p className="text-xs text-white/40 mt-1">{r.description}</p>}
                    {r.impact_summary && (
                      <p className="text-xs text-white/50 mt-1">
                        {r.impact_summary.affected_employees} employee(s) · {r.impact_summary.performance_goals_affected} goal(s)
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {(r.status === "draft" || r.status === "impact_assessed") && (
                      <button onClick={() => assess(r.id)} disabled={busy === r.id}
                        className="text-xs px-3 py-1 rounded border border-yellow-500/30 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20">Assess impact</button>
                    )}
                    {(r.status === "draft" || r.status === "impact_assessed") && (
                      <button onClick={() => approve(r.id)} disabled={busy === r.id}
                        className="text-xs px-3 py-1 rounded border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20">Approve</button>
                    )}
                    {r.status === "approved" && (
                      <button onClick={() => apply(r.id)} disabled={busy === r.id}
                        className="text-xs px-3 py-1 rounded border border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20">Apply</button>
                    )}
                    {r.status === "applied" && (
                      <button onClick={() => rollback(r.id)} disabled={busy === r.id}
                        className="text-xs px-3 py-1 rounded border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20">Rollback</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </section>

        {preview && (
          <div className="fixed inset-0 z-[10060] flex items-center justify-center p-6 bg-black/70 backdrop-blur"
               onClick={() => setPreview(null)}>
            <div onClick={e => e.stopPropagation()}
              className="max-w-2xl w-full max-h-[80vh] overflow-y-auto rounded-xl bg-[#0d0d0d] border border-[#00ff90]/30 p-6 space-y-3">
              <h3 className="text-base font-semibold text-white">Impact preview</h3>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="rounded-lg p-3 bg-white/3 border border-white/5">
                  <p className="text-white/40 uppercase">Employees</p>
                  <p className="text-2xl font-bold text-[#00ff90]">{preview.data?.impact?.affected_employees ?? preview.data?.affected_employees ?? "—"}</p>
                </div>
                <div className="rounded-lg p-3 bg-white/3 border border-white/5">
                  <p className="text-white/40 uppercase">Pending approvals</p>
                  <p className="text-2xl font-bold text-yellow-400">{preview.data?.impact?.pending_approvals_in_scope ?? preview.data?.pending_approvals_in_scope ?? "—"}</p>
                </div>
                <div className="rounded-lg p-3 bg-white/3 border border-white/5">
                  <p className="text-white/40 uppercase">Perf goals</p>
                  <p className="text-2xl font-bold text-blue-400">{preview.data?.impact?.performance_goals_affected ?? preview.data?.performance_goals_affected ?? "—"}</p>
                </div>
              </div>
              <p className="text-xs text-white/40">Affected departments: {(preview.data?.impact?.affected_departments ?? preview.data?.affected_departments ?? []).join(", ") || "—"}</p>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-white/40 uppercase"><tr>
                    <th className="text-left p-2">User</th><th className="text-left p-2">Current</th><th className="text-left p-2">→ New</th>
                  </tr></thead>
                  <tbody>
                    {(preview.data?.impact?.employees ?? preview.data?.employees ?? []).slice(0, 50).map(e => (
                      <tr key={e.user_id} className="border-t border-white/5">
                        <td className="p-2 text-white/60">{e.user_id?.slice(0, 8)}…</td>
                        <td className="p-2 text-white/70">{e.current_dept}</td>
                        <td className="p-2 text-[#00ff90]">{e.will_move_to}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setPreview(null)}
                  className="text-xs px-3 py-1.5 rounded border border-white/15 bg-white/5 text-white/70">Close</button>
                <button onClick={() => approve(preview.id)}
                  className="text-xs px-3 py-1.5 rounded border border-blue-500/30 bg-blue-500/10 text-blue-400">Approve</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MessengerLayout>
  );
}
